import { useState, useEffect, useCallback, useRef } from 'react';

import {
  isAuth0Initialized,
  getAuthState,
  getAccessToken,
  getBackendUrl,
  startLogin,
  logout as auth0Logout,
  getAuthFromFragment,
  setStoredAuth,
  getStoredToken,
  getStoredUser,
  getStoredExpiresAt,
  getStoredAuthOrigin,
  clearStoredAuth,
  refreshAccessToken,
  onTokenRefreshFailure,
  subscribeToAuthChanges,
} from '../lib/auth0-client';
import { clearClientStorageForLogout } from '../lib/logout-storage';
import type { Auth0User, Auth0Session } from '../types/auth0';
import { mapAuth0User } from '../types/auth0';

/** Minimum interval between scheduled refresh checks (30 seconds). */
const MIN_REFRESH_INTERVAL_MS = 30_000;
/** Schedule refresh 5 minutes before expiry. */
const REFRESH_AHEAD_MS = 5 * 60 * 1000;
/** Minimum gap between focus/visibility revalidation checks (CLOUD-732). */
const MIN_REVALIDATE_INTERVAL_MS = 30_000;
/** One retry after an indeterminate check, matching the app's query-client retry:1 policy. */
const INDETERMINATE_RETRY_DELAY_MS = 5_000;

export interface UseAuth0AuthOptions {
  /** Setter function for the user state in your app's store */
  setUser: (user: Auth0User | null) => void;
  /** Setter function for the session state in your app's store */
  setSession: (session: Auth0Session | null) => void;
  /**
   * Setter for the explicit-logout state in your app's store, so the login page
   * can read it. Optional: apps with no federated login page don't need it.
   */
  setExplicitLogout?: (explicitLogout: boolean) => void;
  /**
   * Where Auth0 redirects the browser after a successful logout. Defaults to
   * `window.location.origin`. Pass the canonical user-facing URL for this app
   * (e.g. `https://cloud.ari-os.com`) so logout always lands on the public
   * domain rather than whatever host happened to serve the session.
   *
   * The value must be on Auth0's "Allowed Logout URLs" list for the tenant.
   */
  logoutReturnTo?: string;
}

export interface UseAuth0AuthResult {
  /** The current user, or null if not authenticated */
  user: Auth0User | null;
  /** The current session, or null if not authenticated */
  session: Auth0Session | null;
  /** Sign out the current user */
  signOut: () => Promise<void>;
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Whether the auth state is still loading */
  isLoading: boolean;
  /** Login with redirect to backend's /auth/login */
  loginWithRedirect: (connection?: string) => void;
  /** Get access token from session */
  getAccessToken: () => Promise<string>;
  /** Refresh the auth state manually */
  refreshAuth: () => Promise<void>;
}

/**
 * Hook for managing Auth0 authentication state.
 * Polls the backend's /auth/me endpoint to get session state from cookies.
 *
 * CLOUD-732. Re-validates on focus, visibility, and bfcache restore — not
 * just mount — so a tab that survived a sleep/reboot notices a dead session.
 * An indeterminate check (network/gateway failure) retries once, matching
 * the app's shared query-client retry policy (`libs/ari-lib/src/api/query-client.ts`);
 * this hook can't use that client directly since it manages raw cookie/fetch
 * state, not a react-query resource.
 *
 * @example
 * ```tsx
 * const setUser = useStore((state) => state.setUser);
 * const setSession = useStore((state) => state.setSession);
 *
 * const { isAuthenticated, isLoading, signOut } = useAuth0Auth({
 *   setUser,
 *   setSession,
 * });
 * ```
 */
export function useAuth0Auth({
  setUser,
  setSession,
  setExplicitLogout,
  logoutReturnTo,
}: UseAuth0AuthOptions): UseAuth0AuthResult {
  const [isLoading, setIsLoading] = useState(true);
  const [user, setLocalUser] = useState<Auth0User | null>(null);
  const [session, setLocalSession] = useState<Auth0Session | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Bumped on each updateAuthState call so a slow /auth/me from an
  // earlier run can't clobber state set by a later one.
  const authResolveSeqRef = useRef(0);

  // Consumers may hand in fresh setter functions on every render; routed
  // through refs they cannot change applyAuth's identity, which would re-run
  // the mount effect below on each render — an infinite loop once a session
  // exists, because every pass maps a fresh user object.
  const setUserRef = useRef(setUser);
  const setSessionRef = useRef(setSession);
  const setExplicitLogoutRef = useRef(setExplicitLogout);
  useEffect(() => {
    setUserRef.current = setUser;
    setSessionRef.current = setSession;
    setExplicitLogoutRef.current = setExplicitLogout;
  });

  // -------------------------------------------------------------------
  // Helper: apply auth state into local + store
  // -------------------------------------------------------------------
  // Mirrors the session cookie's logout tombstone, reported by /auth/me, into
  // the app store. Not persisted: the cookie is the source of truth and every
  // load re-reads it.
  const applyExplicitLogout = useCallback((value: boolean) => {
    setExplicitLogoutRef.current?.(value);
  }, []);

  const applyAuth = useCallback(
    (mappedUser: Auth0User | null, sessionData: Auth0Session | null) => {
      // A live session is the opposite state, whatever route the callback landed on.
      if (mappedUser) applyExplicitLogout(false);
      setLocalUser(mappedUser);
      setLocalSession(sessionData);
      setUserRef.current(mappedUser);
      setSessionRef.current(sessionData);
    },
    [applyExplicitLogout]
  );

  // -------------------------------------------------------------------
  // Schedule a proactive refresh before the token expires.
  // -------------------------------------------------------------------
  const scheduleRefresh = useCallback(() => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    // A fragment-origin (Companion embedded WebView) session has no session
    // cookie — the refresh exchange is bound to a different Auth0 client and
    // always fails, forcing a sign-out roughly hourly (CLOUD-749).
    if (getStoredAuthOrigin() === 'fragment') return;

    const expiresAt = getStoredExpiresAt();
    if (!expiresAt) return;

    const msUntilRefresh = expiresAt - Date.now() - REFRESH_AHEAD_MS;
    const delay = Math.max(msUntilRefresh, MIN_REFRESH_INTERVAL_MS);

    refreshTimerRef.current = setTimeout(async () => {
      const newToken = await refreshAccessToken();
      if (newToken) {
        // Update session in state with new token / expiry
        const newExpiresAt = getStoredExpiresAt() ?? Date.now() + 3600000;
        const currentUser = getStoredUser();
        const mappedUser = currentUser ? mapAuth0User(currentUser) : null;

        const sessionData: Auth0Session = {
          access_token: newToken,
          expires_at: newExpiresAt,
        };

        applyAuth(mappedUser, sessionData);

        // Schedule the next refresh
        scheduleRefresh();
      }
      // If refresh failed, the onTokenRefreshFailure callback handles logout
    }, delay);
  }, [applyAuth]);

  // Full sign-out: clears state and redirects through /auth/logout for the tombstone.
  const forceSignOut = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    clearStoredAuth();
    applyAuth(null, null);

    if (isAuth0Initialized()) {
      // Must run synchronously: auth0Logout navigates the browser away.
      clearClientStorageForLogout();
      auth0Logout(logoutReturnTo ?? window.location.origin);
    }
  }, [applyAuth, logoutReturnTo]);

  // Main auth-state resolver (runs on mount, and when called manually).
  // `force` skips the cached-token short-circuit for a real /auth/me check.
  // `isRetry` marks the one scheduled retry after an indeterminate check, so
  // that retry can't schedule another one of its own.
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const updateAuthState = useCallback(
    async (opts?: { force?: boolean; isRetry?: boolean }) => {
      const resolveSeq = ++authResolveSeqRef.current;
      const isStale = () => resolveSeq !== authResolveSeqRef.current;

      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }

      if (!isAuth0Initialized()) {
        if (!isStale()) setIsLoading(false);
        return;
      }

      try {
        // First check URL fragment (after redirect from callback)
        const fragmentAuth = getAuthFromFragment();
        if (fragmentAuth?.authenticated && fragmentAuth.user && fragmentAuth.access_token) {
          const expiresAt = fragmentAuth.expires_at ?? Date.now() + 3600000;

          // Store token for future API calls
          setStoredAuth(
            fragmentAuth.access_token,
            fragmentAuth.user,
            fragmentAuth.refresh_token,
            expiresAt,
            'fragment'
          );

          if (isStale()) return;
          const mappedUser = mapAuth0User(fragmentAuth.user);
          const sessionData: Auth0Session = {
            access_token: fragmentAuth.access_token,
            expires_at: expiresAt,
          };

          applyAuth(mappedUser, sessionData);
          setIsLoading(false);
          scheduleRefresh();
          return;
        }

        const storedToken = getStoredToken();
        const storedUser = getStoredUser();
        const hadCachedSession = !!(storedToken && storedUser);
        const storedExpiresAtNow = getStoredExpiresAt();
        // True wall-clock expiry, unlike isTokenExpired()'s 5-min refresh buffer.
        const isPastActualExpiry = storedExpiresAtNow !== null && Date.now() >= storedExpiresAtNow;

        // Expired-by-clock cache can't be trusted — fall through to /auth/me.
        if (hadCachedSession && !opts?.force && !isPastActualExpiry) {
          if (isStale()) return;
          const expiresAt = storedExpiresAtNow ?? Date.now() + 3600000;
          const mappedUser = mapAuth0User(storedUser);
          const sessionData: Auth0Session = {
            access_token: storedToken,
            expires_at: expiresAt,
          };

          applyAuth(mappedUser, sessionData);
          setIsLoading(false);
          scheduleRefresh();
          return;
        }

        // Cookie-driven hydration: the .ari-os.com session cookie rides
        // with this request and any sibling app picks up the same session.
        const authState = await getAuthState();
        if (isStale()) return;

        if (authState?.authenticated && authState.user && authState.access_token) {
          const expiresAt = authState.expires_at ?? Date.now() + 3600000;
          setStoredAuth(authState.access_token, authState.user, authState.refresh_token, expiresAt, 'cookie');

          const mappedUser = mapAuth0User(authState.user);
          const sessionData: Auth0Session = {
            access_token: authState.access_token,
            expires_at: expiresAt,
          };

          applyAuth(mappedUser, sessionData);
          scheduleRefresh();
        } else if (authState !== null) {
          // A definitive 200 answer: no session found via cookie/header.
          if (hadCachedSession) {
            // A fragment-origin token has no guaranteed cookie for /auth/me
            // to see — that's true for the Companion's embedded WebView, and
            // also for a browser tab that just consumed its own login
            // fragment before this same check would rehydrate it as
            // cookie-origin. Trust it until it actually expires instead of
            // signing out on a false negative (CLOUD-749).
            if (getStoredAuthOrigin() === 'fragment' && !isPastActualExpiry) return;

            // A cross-origin (cookie-invisible) session can still be alive
            // via its in-memory refresh token — try that before giving up.
            const refreshedToken = await refreshAccessToken();
            if (!isStale() && refreshedToken) {
              return updateAuthState();
            }
            console.warn('[@nexus/supabase] Session no longer valid — logging user out');
            forceSignOut();
          } else {
            // Login page reads this before the router mounts, so it never
            // hands off against a deliberate sign-out.
            applyExplicitLogout(authState.explicit_logout === true);
            applyAuth(null, null);
          }
        } else if (hadCachedSession) {
          // Indeterminate (network/gateway) failure, not a confirmed
          // sign-out — keep the cached session and retry once.
          console.warn('[@nexus/supabase] Revalidation failed — keeping cached session');
          if (!opts?.isRetry) {
            retryTimerRef.current = setTimeout(() => {
              updateAuthState({ force: true, isRetry: true }).catch((error) => {
                console.error('[@nexus/supabase] Retry after indeterminate check failed:', error);
              });
            }, INDETERMINATE_RETRY_DELAY_MS);
          }
        } else {
          applyAuth(null, null);
        }
      } catch (error) {
        if (isStale()) return;
        console.error('[@nexus/supabase] Error checking auth state:', error);
        applyAuth(null, null);
      } finally {
        if (!isStale()) setIsLoading(false);
      }
    },
    [applyAuth, applyExplicitLogout, scheduleRefresh, forceSignOut]
  );

  const signOut = useCallback(async () => {
    forceSignOut();
  }, [forceSignOut]);

  // -------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------

  // Check initial auth state
  useEffect(() => {
    updateAuthState();
  }, [updateAuthState]);

  // Register refresh-failure handler: disconnect the user automatically
  useEffect(() => {
    const cleanup = onTokenRefreshFailure(() => {
      console.warn('[@nexus/supabase] Token refresh failed — logging user out');
      forceSignOut();
    });

    return cleanup;
  }, [forceSignOut]);

  // ROPC login mutates in-memory auth without hitting /auth/callback,
  // so we re-render through the same path mount uses.
  useEffect(() => {
    return subscribeToAuthChanges(() => {
      updateAuthState();
    });
  }, [updateAuthState]);

  // Re-checks the session on focus, visibility, and bfcache restore.
  const lastRevalidateAtRef = useRef(0);
  useEffect(() => {
    // A dev server on localhost cannot send the session cookie to a remote backend, so this check signs out a live session (CLOUD-770).
    if (import.meta.env.DEV && getBackendUrl().startsWith('http')) return;

    const revalidate = () => {
      const now = Date.now();
      if (now - lastRevalidateAtRef.current < MIN_REVALIDATE_INTERVAL_MS) return;
      lastRevalidateAtRef.current = now;
      updateAuthState({ force: true }).catch((error) => {
        console.error('[@nexus/supabase] Focus/visibility revalidation failed:', error);
      });
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') revalidate();
    };
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) revalidate();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', revalidate);
    window.addEventListener('pageshow', onPageShow);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', revalidate);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [updateAuthState]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
      }
    };
  }, []);

  const loginWithRedirect = useCallback((connection?: string) => {
    if (!isAuth0Initialized()) {
      console.error('[@nexus/supabase] Auth0 not initialized');
      return;
    }

    startLogin(connection);
  }, []);

  const getAccessTokenCallback = useCallback(async (): Promise<string> => {
    try {
      return await getAccessToken();
    } catch {
      return '';
    }
  }, []);

  return {
    user,
    session,
    signOut,
    isAuthenticated: !!user,
    isLoading,
    loginWithRedirect,
    getAccessToken: getAccessTokenCallback,
    refreshAuth: updateAuthState,
  };
}
